import React from 'react';
import { ArrowBigDown, Mail, Phone, Instagram, Linkedin, Twitter, MapPin, Github, Youtube, Shield, Star } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const location = useLocation();
  const navigate = useNavigate();

  const scrollToSection = (sectionId: string) => {
    if (location.pathname !== '/') {
      navigate('/');
      // Use setTimeout to ensure navigation completes before scrolling
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
      return;
    }
    
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-secondary text-secondary-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="md:col-span-2">
            <div className="flex items-center space-x-2 mb-4">
              <ArrowBigDown className="w-6 h-6 text-primary" strokeWidth={2.5} />
              <span className="font-medium text-xl">Arrow Drop Foundation</span>
            </div>
            <p className="mb-6 text-muted-foreground">
              Section 8 Company License No: 167793<br />
              NPO Darpan ID : TN/2025/0623595<br />
              Corporate ID : U88900TZ2025NPL034526<br />
              Certificate of Recognition: DIPP205239<br />
              Working towards creating positive change in our communities.
            </p>
            <div className="flex space-x-4">
              <a 
                href="https://github.com/arrowdropfoundation" 
                className="p-2 rounded-full bg-primary/10 hover:bg-primary/20 transition-colors"
                aria-label="Github"
              >
                <Github className="w-5 h-5 text-primary" />
              </a>
              <a 
                href="https://www.linkedin.com/company/arrowdropfoundation/" 
                className="p-2 rounded-full bg-primary/10 hover:bg-primary/20 transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-5 h-5 text-primary" />
              </a>
              <a 
                href="https://www.youtube.com/@ARROWDROPFOUNDATION" 
                className="p-2 rounded-full bg-primary/10 hover:bg-primary/20 transition-colors"
                aria-label="YouTube"
              >
                <Youtube className="w-5 h-5 text-primary" />
              </a>
              <a 
                href="https://x.com/ArrowDrop_NGO" 
                className="p-2 rounded-full bg-primary/10 hover:bg-primary/20 transition-colors"
                aria-label="Twitter"
              >
                <Twitter className="w-5 h-5 text-primary" />
              </a>
              <a 
                href="https://www.instagram.com/arrowdropfoundation/" 
                className="p-2 rounded-full bg-primary/10 hover:bg-primary/20 transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5 text-primary" />
              </a>
            </div>
          </div>

          <div>
            <h3 className="font-medium text-lg mb-4">Quick Links</h3>
            <ul className="space-y-2">
              <li>
                <button 
                  onClick={() => scrollToSection('about')}
                  className="text-muted-foreground hover:text-primary transition-colors text-left"
                >
                  About Us
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('mission')}
                  className="text-muted-foreground hover:text-primary transition-colors text-left"
                >
                  Our Mission
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('roadmap')}
                  className="text-muted-foreground hover:text-primary transition-colors text-left"
                >
                  Roadmap
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('contact')}
                  className="text-muted-foreground hover:text-primary transition-colors text-left"
                >
                  Contact
                </button>
              </li>
              <li>
                <Link to="/transparency" className="text-muted-foreground hover:text-primary transition-colors">
                  Transparency
                </Link>
              </li>
              <li>
                <Link to="/privacy-policy" className="text-muted-foreground hover:text-primary transition-colors">
                  Privacy Policy
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="font-medium text-lg mb-4">Contact Info</h3>
            <ul className="space-y-3">
              <li className="flex items-start">
                <Phone className="w-5 h-5 text-primary mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-muted-foreground">+91 9698862115</span>
              </li>
              <li className="flex items-start">
                <Mail className="w-5 h-5 text-primary mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-muted-foreground">arrowdropfoundation@gmail.com</span>
              </li>
              <li className="flex items-start">
                <MapPin className="w-5 h-5 text-primary mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-muted-foreground">
                  1/71 B, Perfect Garden,<br />
                  Andakkapalayam, Vellanipatti Post,<br />
                  Coimbatore, Tamil Nadu-641048
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Trust Indicators Section */}
        <div className="border-t border-gray-700 mt-8 pt-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-center md:text-left">
              <p className="text-muted-foreground">&copy; {currentYear} Arrow Drop Foundation. All rights reserved.</p>
            </div>
            
            <div className="flex items-center gap-4">
              {/* ScamAdviser */}
              <a 
                href="https://scamadviser.com/check-website/arrowdropfoundation.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 px-4 py-2 bg-card hover:bg-primary/10 rounded-lg border border-border transition-colors duration-300"
                aria-label="Check our website on ScamAdviser"
              >
                <div className="flex items-center justify-center w-10 h-10 bg-blue-600 rounded-lg">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-foreground">
                    Verified by
                  </span>
                  <span className="text-xs text-muted-foreground">
                    ScamAdviser
                  </span>
                </div>
              </a>

              {/* Trustpilot */}
              <a 
                href="https://www.trustpilot.com/review/arrowdropfoundation.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 px-4 py-2 bg-card hover:bg-primary/10 rounded-lg border border-border transition-colors duration-300"
                aria-label="Read our reviews on Trustpilot"
              >
                <div className="flex items-center justify-center w-10 h-10 bg-green-600 rounded-lg">
                  <Star className="w-6 h-6 text-white fill-white" />
                </div>
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-foreground">
                    Reviews on
                  </span>
                  <span className="text-xs text-muted-foreground">
                    Trustpilot
                  </span>
                </div>
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;